/* ═══════════════════════════════════════════════════════════
   sp-predictive-search.js
   Controlador de la búsqueda predictiva (overlay pantalla completa).
   · Debounce + AbortController (descarta respuestas obsoletas / fuera de orden)
   · Section Rendering API → inyecta HTML de sections/sp-predictive-search.liquid
   · Patrón combobox: flechas para navegar, aria-activedescendant, Enter para ir
   · Esc / backdrop para cerrar, focus-trap ligero, bloqueo de scroll del body
   Mejora progresiva: sin JS la lupa navega a /search y el form envía a /search.
   ═══════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var root = document.getElementById('SpPredictiveSearch');
  if (!root) return;

  var input    = root.querySelector('[data-predictive-input]');
  var results  = root.querySelector('[data-predictive-results]');
  var closeBtn = root.querySelector('.sp-psearch__close');
  var clearBtn = root.querySelector('[data-psearch-clear]');
  var openers  = document.querySelectorAll('[data-psearch-open]');
  var closers  = root.querySelectorAll('[data-psearch-close]');

  function toggleClear() {
    if (!clearBtn) return;
    clearBtn.classList.toggle('is-visible', input.value.length > 0);
  }

  var predictiveUrl = root.getAttribute('data-predictive-url') || '/search/suggest';
  var sectionId     = root.getAttribute('data-section-id') || 'sp-predictive-search';
  var limit         = parseInt(root.getAttribute('data-limit') || '6', 10);
  var minChars      = parseInt(root.getAttribute('data-min-chars') || '2', 10);

  var lastOpener  = null;
  var controller  = null;
  var debounce    = null;
  var activeIndex = -1;

  /* ───────── abrir / cerrar ───────── */
  function open(trigger) {
    /* Portal: el overlay se define dentro de header.liquid, que vive en el
       header-group (position:sticky → crea su propio stacking context). Ahí
       dentro, el z-index:1000 del overlay solo compite CONTRA otros hijos del
       header, no contra la barra de anuncio (sección hermana fuera del grupo),
       que por eso quedaba encima. Moverlo a <body> lo saca de ese contexto y su
       z-index vuelve a competir a nivel raíz. Idempotente: solo mueve una vez. */
    if (root.parentNode !== document.body) {
      document.body.appendChild(root);
    }
    lastOpener = trigger || null;
    root.hidden = false;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    openers.forEach(function (o) { o.setAttribute('aria-expanded', 'true'); });
    requestAnimationFrame(function () {
      input.focus();
      toggleClear();
      var q = input.value.trim();
      if (q.length >= minChars) fetchResults(q);
    });
  }

  function close() {
    root.classList.remove('is-open');
    root.classList.remove('is-loading');
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    input.setAttribute('aria-expanded', 'false');
    openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
    if (controller) { controller.abort(); controller = null; }
    clearResults();
    if (lastOpener && typeof lastOpener.focus === 'function') lastOpener.focus();
  }

  openers.forEach(function (o) {
    o.addEventListener('click', function (e) { e.preventDefault(); open(o); });
  });
  closers.forEach(function (c) {
    c.addEventListener('click', function (e) { e.preventDefault(); close(); });
  });

  /* ───────── resultados ───────── */
  function clearResults() {
    results.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    activeIndex = -1;
  }

  function fetchResults(q) {
    if (controller) controller.abort();
    controller = new AbortController();
    root.classList.add('is-loading');

    var url = predictiveUrl
      + '?q=' + encodeURIComponent(q)
      + '&section_id=' + encodeURIComponent(sectionId)
      + '&resources[type]=product'
      + '&resources[limit]=' + limit;

    fetch(url, { signal: controller.signal })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        results.innerHTML = html;
        root.classList.remove('is-loading');
        input.setAttribute('aria-expanded', 'true');
        setActive(-1);
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return; /* petición obsoleta: ignorar */
        root.classList.remove('is-loading');
        results.innerHTML =
          '<p class="sp-psearch__empty">No se pudo cargar la búsqueda. Presiona Enter para ver todos los resultados.</p>';
      });
  }

  input.addEventListener('input', function () {
    toggleClear();
    var q = input.value.trim();
    clearTimeout(debounce);
    if (q.length < minChars) {
      if (controller) { controller.abort(); controller = null; }
      root.classList.remove('is-loading');
      clearResults();
      return;
    }
    debounce = setTimeout(function () { fetchResults(q); }, 220);
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      input.value = '';
      toggleClear();
      if (controller) { controller.abort(); controller = null; }
      root.classList.remove('is-loading');
      clearResults();
      input.focus();
    });
  }

  /* ───────── navegación por teclado (combobox) ───────── */
  function options() {
    return Array.prototype.slice.call(results.querySelectorAll('[role="option"]'));
  }

  function setActive(i) {
    var opts = options();
    opts.forEach(function (o, idx) {
      var on = idx === i;
      o.setAttribute('aria-selected', on ? 'true' : 'false');
      o.classList.toggle('is-active', on);
    });
    if (i >= 0 && opts[i]) {
      input.setAttribute('aria-activedescendant', opts[i].id);
      opts[i].scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
    activeIndex = i;
  }

  root.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }

    /* focus-trap ligero: mantener Tab dentro del panel */
    if (e.key === 'Tab') {
      var focusables = [input, closeBtn].filter(Boolean);
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }

    var opts = options();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (opts.length) setActive((activeIndex + 1) % opts.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (opts.length) setActive((activeIndex - 1 + opts.length) % opts.length);
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && opts[activeIndex]) {
        var link = opts[activeIndex].querySelector('a');
        if (link) { e.preventDefault(); window.location.href = link.href; }
      }
      /* si no hay opción activa: el form envía a /search?q=… (sin type) */
    }
  });
})();
